import React from "react";
import {
	SEGMENT_BZA,
	URL_BZA_AIRCRAFT_HISTORY_DATA,
	URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
	URL_BZA_OPERATOR_AIRPORT_DATA,
	URL_BZA_OPERATOR_CONTACT_DATA,
} from "../constants";
import { Faq } from "../sections/faq/Faq";
import { Pricing } from "../sections/pricing/Pricing";
import { ProductSlider } from "../sections/ProductSlider";
import { ProductTabs } from "../sections/ProductTabs";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { Trial } from "../sections/Trial";
import { Upgrades } from "../sections/Upgrades";
import { Hero } from "../sections/hero/Hero";
import { productSegmentPageProps } from "../../common/types/product";

export default function BusinessAircraftData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="Access data on 29,000+ business jets worldwide, with detailed insights at the tail level."
				packages={[
					{
						label: SEGMENT_BZA,
						features: [
							"Over 29,000 aircraft profiles",
							"Tail-level data, including specifications, status and engine data",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Aircraft Overview",
						description:
							"Gain a comprehensive, up-to-date business jet data including:",
						list: [
							"IATA code",
							"Aircraft registration, C/N, MSN and variant info",
							"Status info (who is operating the aircraft, is the aircraft active, aircraft age and MTOW)",
							"Aircraft insights (the exact model of aircraft, production line, and aircraft role)",
							"Aircraft hexcode",
							"Engine details (which engines are powering the aircraft, including engine type and subtype)",
							"ICAO engine emission databank id code",
							"Aircraft construction site",
							"Dates (when was the aircraft ordered and by whom, date of the first flight and delivery date)",
							"Seat info (number of seats, max. Abreast, pitch-in, in-seat video, in-seat power supply, if the aircraft has a usb port and which type; if the aircraft supports wireless charging, in-flight connectivity)",
							"Flight hours & cycles (total number of hours & cycles, avg. Stage length, avg. Annual hours & cycles, avg. Daily utilisation – including historical and forecasted data)",
						],
						visual: {
							url: "/static/img/product/business-jet-aircraft-data/aircraft-profile-overview.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search for business jets worldwide using a range of filters, such as location, operator, aircraft type, engine type, seat configuration and more."
				slides={[
					{
						title: "Geographical",
						description: "Search for business jets by location:",
						list: [
							"Continent",
							"Country",
							"Operator region ",
							"Aircraft register",
							"Its current location",
						],
						visual: {
							title: "List of all inactive aircraft currently located at Teterboro airport",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-geography.jpg",
						},
					},
					{
						title: "Aircraft Base",
						description: "Search for business jets by:",
						list: [
							"Airport base",
							"Region",
							"Continent",
							"Country",
							"Subdivision",
							"Metro group",
						],
						visual: {
							title: "List of all business jets based at White Plains Westchester County",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-aircraft-base.jpg",
						},
					},
					{
						title: "Operator",
						description:
							"Decide if you’d like to include or exclude multiple operators, and search for business jets by:",
						list: [
							"Operator name",
							"Operator type",
							"Delivery customer",
							"Future operator",
						],
						visual: {
							title: "A list of all Flight Department Operators across North America",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-operator.jpg",
						},
					},
					{
						title: "Aircraft Type",
						description: "Search for business jets by:",
						list: [
							"Aircraft market group (large jets, midsize jets, very light jets, etc.)",
							"Aircraft manufacturer",
							"Aircraft production line",
							"Aircraft family/type/model/variant",
							"IATA code",
							"Aircraft role",
							"Cargo loading configuration (nose loader, tail loader, etc.)",
						],
						visual: {
							title: "List of all active ultra-long-range Gulfstream jets",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-aircraft-type.jpg",
						},
					},
					{
						title: "Aircraft",
						description: "Search for business jets by:",
						list: [
							"Aircraft registration",
							"Fleet number",
							"Line/serial number",
							"Aircraft hexcode",
							"Aircraft status/sub-status",
							"MTOW",
							"Construction site",
							"Lavatory/lavatory belted ",
							"Last year of interior refurbishment",
							"Last year of exterior refurbishment",
							"Aircraft certification (whether the aircraft is registered for Part 135 or Part 91 operations)",
						],
						visual: {
							title: "Profile overview of the aircraft registration N615TT",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-aircraft.jpg",
						},
					},
					{
						title: "Date",
						description: "Look into business jets based on:",
						list: [
							"Date built",
							"Delivery date",
							"Operational start",
							"Projected retirement date",
							"Storage date",
							"Scrapped or written off date",
						],
						visual: {
							title: "List of all business jets scrapped from January 1st, 2025, to June 1st, 2025",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-date.jpg",
						},
					},
					{
						title: "Orders",
						description: "Look into business jets by:",
						list: [
							"Ordering entity",
							"Order date",
							"Cancellation date",
						],
						visual: {
							title: "An overview of Flexjet’s jets on order",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-orders.jpg",
						},
					},
					{
						title: "Seating",
						description: "Search for business jets by:",
						list: [
							"Number of seats",
							"In-flight connectivity info",
						],
						visual: {
							title: "List of all active business jets equipped with 12 seats",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-seating.jpg",
						},
					},
					{
						title: "Engines",
						description: "Search for business jets by:",
						list: [
							"Engine manufacturer",
							"Engine master series",
							"Engine family",
							"Engine type",
							"Engine subtype",
							"ICAO Engine Emission Databank ID",
						],
						visual: {
							title: "List of all inactive business jets powered by Williams International engines",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-engines.jpg",
						},
					},
					{
						title: "Ownership",
						description: "Search for business jets by:",
						list: [
							"Owner name",
							"Owner/manager, SPV/trust",
							"Ownership type",
						],
						visual: {
							title: "Overview of owners and manager of European business jet operators",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-ownership.jpg",
						},
					},
					{
						title: "Meta",
						description:
							"Search for all aircraft added to our database within a specific time period.",
						visual: {
							title: "List of all aircraft added in the last 15 days",
							url: "/static/img/product/business-jet-aircraft-data/aircraft-search-by-meta.jpg",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Operator & Airport Data",
						content:
							"Operator & Airport Data – Get access to more than 52,000 business jet operators and over 14,000 airport profiles ",
						url: URL_BZA_OPERATOR_AIRPORT_DATA,
					},
					{
						title: "Operator Contact Data",
						content:
							"Get a detailed contacts overview for almost any active business jet operator worldwide.",
						url: URL_BZA_OPERATOR_CONTACT_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into business jet aircraft owners, managers and SPVs.",
						url: URL_BZA_AIRCRAFT_OWNERSHIP_DATA,
					},
					{
						title: "Aircraft Historical Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_BZA_AIRCRAFT_HISTORY_DATA,
					},
				]}
			>
				<Trial
					description="Get full, unrestricted access to all of ch-aviation’s news and data for 14 days. Our no-commitment guarantee:"
					subscribeLink={subscribeLink}
				/>
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={["Global coverage: Access 29,000+ business jet aircraft"]}
				subscribeLink={subscribeLink}
				downloadsDescription="Our Business Jet Aircraft Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download these data sets?",
						content:
							"The Business Jets Aircraft Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content:
							"Yes, Business Jet Aircraft Data can be purchased as a data feed for seamless integration into your own system. ch-aviation also provides full support for the integration process. To learn more about data feeds, click here.",
					},
					{
						title: "How often is the data updated?",
						content:
							"The Business Jet Aircraft Data is being updated weekly, ensuring you always have the latest information.",
					},
				]}
			/>
		</>
	);
}
